import { ChargeType } from "../graphql/generated";

// Same rules as refreshDependentRateBases in the backend invoice edit.

export const PERCENTAGE_OF_TOTAL = "% of total";
export const PERCENTAGE_OF_LINE_HAUL = "% of line haul";

type ChargeLike = {
  rate: number;
  unit: number;
  unitType?: string | null;
  taxable?: boolean | null;
  chargeType?: ChargeType | null;
  shipmentId?: string | null;
};

const total = (charges: ChargeLike[]) =>
  charges.reduce((sum, charge) => sum + charge.rate * charge.unit, 0);

function refreshBase<T extends ChargeLike>(
  charges: T[],
  shouldRefresh: (charge: T) => boolean,
  baseCharges: (charge: T, sameOrder: T[]) => T[]
): T[] {
  return charges.map((charge) => {
    if (!shouldRefresh(charge)) {
      return charge;
    }
    const sameOrder = charges.filter(
      (c) => (c.shipmentId || "") === (charge.shipmentId || "")
    );
    const base = total(baseCharges(charge, sameOrder));
    return charge.rate === base ? charge : { ...charge, rate: base };
  });
}

// Line haul based lines first, then total based lines, then tax.
export function refreshDependentRateBases<T extends ChargeLike>(
  charges: T[]
): T[] {
  const lineHaulBased = refreshBase(
    charges,
    (charge) => charge.unitType === PERCENTAGE_OF_LINE_HAUL,
    (charge, sameOrder) =>
      sameOrder.filter(
        (c) =>
          c.chargeType === ChargeType.Linehaul &&
          (charge.chargeType !== ChargeType.Tax || c.taxable !== false)
      )
  );
  const totalBased = refreshBase(
    lineHaulBased,
    (charge) =>
      charge.chargeType !== ChargeType.Tax &&
      charge.unitType === PERCENTAGE_OF_TOTAL,
    (_charge, sameOrder) =>
      sameOrder.filter(
        (c) =>
          c.chargeType !== ChargeType.Tax &&
          c.unitType !== PERCENTAGE_OF_TOTAL
      )
  );
  return refreshBase(
    totalBased,
    (charge) =>
      charge.chargeType === ChargeType.Tax &&
      charge.unitType === PERCENTAGE_OF_TOTAL,
    (_charge, sameOrder) =>
      sameOrder.filter(
        (c) => c.chargeType !== ChargeType.Tax && c.taxable !== false
      )
  );
}
